import React, { useState, useMemo } from "react";
import { UserInputsForm } from "./components/UserInputsForm";
import type { UserInputValues } from "@/lib/utils/marketSimilarity/types";
import {
  useListCommodityKeys,
  useFetchSimilarityData,
  useFetchHistoricInputs,
} from "@/lib/api/marketSimilarity";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SaveTextFileButton from "./components/SaveTextFileButton";
import { rowsToCSVString } from "@/lib/utils/marketSimilarity/csvUtils";
import { ResultsTable } from "./components/ResultsTable";
import { LineChartPanel } from "./components/LineChartPanel";
import { ChartModal } from "./components/ChartModal";
import { toTableRows } from "@/lib/utils/marketSimilarity/tableUtils";
import { useProcessedLineChartData } from "./utils/useProcessedLineChartData";
import { useToast } from "@/components/ui/use-toast";
import type { VisibilityState } from "@tanstack/react-table";
const USER_INPUTS_STORAGE_KEY = "userInputValues";

// Columns hidden by default: euclideanRank, cosineRank
// Columns visible by default: all feature data columns, avgCorrel, and everything else
const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  euclideanRank: false,
  cosineRank: false,
};

export default function MarketSimilarityPage() {
  const { toast } = useToast();
  const [selectedTab, setSelectedTab] = useState("raw");
  const [modalDate, setModalDate] = useState<string | null>(null);
  const [defaultInputs] = useState<UserInputValues | undefined>(() => {
    if (typeof window !== "undefined") {
      const savedInputsString = localStorage.getItem(USER_INPUTS_STORAGE_KEY);
      if (savedInputsString != null) {
        try {
          return JSON.parse(savedInputsString) as UserInputValues;
        } catch {
          return undefined;
        }
      }
    }
    return undefined;
  });

  // Fetch commodity keys
  const commodityKeysResult = useListCommodityKeys();
  const commodityKeys = commodityKeysResult?.data?.data?.data || [];

  // Mutation for fetching similarity data
  const fetchSimilarityMutation = useFetchSimilarityData();

  // Mutation for fetching historic inputs
  const fetchHistoricInputsMutation = useFetchHistoricInputs();

  // Extract table rows from the mutation data
  const { rawRows, clusteredRows } = useMemo(() => {
    if (fetchSimilarityMutation.data?.data) {
      return toTableRows(fetchSimilarityMutation.data.data);
    }
    return { rawRows: [], clusteredRows: [] };
  }, [fetchSimilarityMutation.data]);

  // Extract line chart data
  const lineChartData = useMemo(() => {
    return fetchSimilarityMutation.data?.data?.lineChart ?? [];
  }, [fetchSimilarityMutation.data]);

  // Process line chart data for use in chart and modal
  const processedData = useProcessedLineChartData(lineChartData);

  const onSubmit = async (userInputValues: UserInputValues) => {
    // Strip off dateString and date since we want to default to "today"
    const { dateString, date, ...vals } = { ...userInputValues };
    localStorage.setItem(USER_INPUTS_STORAGE_KEY, JSON.stringify(vals));
    const result = await fetchSimilarityMutation.mutateAsync(userInputValues);
    if (!!result?.success && result.data?.statuses?.ts2vec?.usedAdjacentDate) {
      toast({
        title: "TS2Vec used adjacent date",
        description: `Adjusted to ${result.data.statuses.ts2vec.chosen_date}`
      });
    }
  };

  const loading = fetchSimilarityMutation.isLoading;
  const error = fetchSimilarityMutation.error
    ? String(fetchSimilarityMutation.error)
    : null;

  return (
    <div className="bg-white text-black p-4">
      <h2 className="text-xl font-semibold mb-4">Market Similarity</h2>

      <UserInputsForm
        loading={loading}
        onSubmit={onSubmit}
        defaultValues={defaultInputs}
        commodityKeys={commodityKeys}
        onFetchHistoricInputs={(dateString, commodityKey, numRallyDays) => {
          fetchHistoricInputsMutation.mutate({ dateString, commodityKey, numRallyDays });
        }}
        historicInputsLoading={fetchHistoricInputsMutation.isLoading}
        historicInputsError={
          fetchHistoricInputsMutation.error
            ? String(fetchHistoricInputsMutation.error)
            : fetchHistoricInputsMutation.data &&
              fetchHistoricInputsMutation.data.success === false
            ? fetchHistoricInputsMutation.data.message
            : null
        }
        historicInputsData={
          fetchHistoricInputsMutation.data &&
          fetchHistoricInputsMutation.data.success === true
            ? fetchHistoricInputsMutation.data.data
            : null
        }
      />

      {error && (
        <div
          role="alert"
          className="mt-4 text-red-700 bg-red-50 border border-red-200 rounded p-3"
        >
          {error}
        </div>
      )}

      <div className="mt-10" />

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="raw">Raw Data</TabsTrigger>
          <TabsTrigger value="clustered">Clustered Data</TabsTrigger>
          <TabsTrigger value="graph">Graph</TabsTrigger>
        </TabsList>

        {selectedTab !== "graph" && (
          <div className="flex flex-row items-center justify-end mt-4 mb-4">
            <SaveTextFileButton
              disabled={
                loading ||
                (selectedTab === "raw"
                  ? rawRows.length === 0
                  : clusteredRows.length === 0)
              }
              textData={
                selectedTab === "raw"
                  ? rowsToCSVString(rawRows)
                  : rowsToCSVString(clusteredRows)
              }
              label={"Save table to file"}
              filename={selectedTab === "raw" ? "rawTable.csv" : "clusteredTable.csv"}
            />
          </div>
        )}

        <TabsContent value="raw">
          <ResultsTable
            statuses={fetchSimilarityMutation.data?.data?.statuses}
            rows={rawRows}
            loading={loading}
            isClustered={false}
            defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
          />
        </TabsContent>

        <TabsContent value="clustered">
          <ResultsTable
            statuses={fetchSimilarityMutation.data?.data?.statuses}
            rows={clusteredRows}
            loading={loading}
            isClustered={true}
            defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
            onDateClick={setModalDate}
          />
        </TabsContent>

        <TabsContent value="graph">
          <LineChartPanel processedData={processedData} loading={loading} />
        </TabsContent>
      </Tabs>

      <ChartModal
        open={modalDate !== null}
        onClose={() => setModalDate(null)}
        processedData={processedData}
        date={modalDate ?? ""}
        loading={loading}
      />
    </div>
  );
}
